import { useLayoutEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themes_animated);

const officeData = [
    { year: '2019', sydney: 18500, hongKong: 32000, tokyo: 15000, singapore: 22000 },
    { year: '2020', sydney: 17200, hongKong: 29500, tokyo: 14200, singapore: 20500 },
    { year: '2021', sydney: 18800, hongKong: 30100, tokyo: 14800, singapore: 21800 },
    { year: '2022', sydney: 21000, hongKong: 31500, tokyo: 16200, singapore: 24500 },
    { year: '2023', sydney: 23400, hongKong: 33200, tokyo: 17800, singapore: 26800 },
    { year: '2024', sydney: 25100, hongKong: 34800, tokyo: 19100, singapore: 28300 },
];

const retailData = [
    { year: '2019', sydney: 12000, hongKong: 45000, tokyo: 11000, singapore: 16000 },
    { year: '2020', sydney: 10500, hongKong: 38000, tokyo: 9800, singapore: 13500 },
    { year: '2021', sydney: 11200, hongKong: 39500, tokyo: 10300, singapore: 14200 },
    { year: '2022', sydney: 12800, hongKong: 42000, tokyo: 11500, singapore: 16800 },
    { year: '2023', sydney: 14100, hongKong: 44500, tokyo: 12800, singapore: 18200 },
    { year: '2024', sydney: 15300, hongKong: 46200, tokyo: 13900, singapore: 19500 },
];

const SampleBarChart = ({ containerId, data, markets, title, source }: any) => {
    const chartRef = useRef<am4charts.XYChart | null>(null);

    useLayoutEffect(() => {
        const chart = am4core.create(containerId, am4charts.XYChart);
        chartRef.current = chart;

        chart.data = data;
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'year';
        categoryAxis.title.text = 'Year';

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = 'USD per sqm';
        valueAxis.numberFormatter.numberFormat = '#,###';

        const colors = ['#1D4FA3', '#D6342F', '#2E8B57', '#FF8C00'];
        markets.forEach((market: string, i: number) => {
            const series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueY = market.key;
            series.dataFields.categoryX = 'year';
            series.name = market.label;
            series.columns.template.tooltipText = `${market.label}: {valueY} USD/sqm`;
            series.columns.template.fill = am4core.color(colors[i]);
            series.columns.template.stroke = am4core.color(colors[i]);
            series.columns.template.width = am4core.percent(60);
        });

        chart.cursor = new am4charts.XYCursor();

        return () => { chart.dispose(); };
    }, []);

    return (
        <div style={{ padding: '20px 30px' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1c4050' }}>{title}</h3>
            <div id={containerId} style={{ width: '100%', height: 380 }} />
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Source: {source}</p>
        </div>
    );
};

const PricingInformation = () => {
    return (
        <div style={{ padding: '24px 0' }}>
            <div style={{ padding: '0 30px', marginBottom: 32 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1c4050', marginBottom: 8 }}>
                    APAC Commercial Real Estate — Pricing Information
                </h2>
                <p style={{ color: '#6e7189', fontSize: 14, maxWidth: 720 }}>
                    Average transaction pricing per square metre across key APAC gateway markets.
                    Data reflects prime grade assets in central business districts.
                </p>
            </div>

            <SampleBarChart
                containerId="pricing-office-chart"
                data={officeData}
                markets={[
                    { key: 'sydney', label: 'Sydney' },
                    { key: 'hongKong', label: 'Hong Kong' },
                    { key: 'tokyo', label: 'Tokyo' },
                    { key: 'singapore', label: 'Singapore' },
                ]}
                title="Prime Office — Average Capital Value (USD/sqm)"
                source="Ascend Invest Research, JLL, CBRE (Sample Data)"
            />

            <SampleBarChart
                containerId="pricing-retail-chart"
                data={retailData}
                markets={[
                    { key: 'sydney', label: 'Sydney' },
                    { key: 'hongKong', label: 'Hong Kong' },
                    { key: 'tokyo', label: 'Tokyo' },
                    { key: 'singapore', label: 'Singapore' },
                ]}
                title="Prime Retail — Average Capital Value (USD/sqm)"
                source="Ascend Invest Research, Savills, Knight Frank (Sample Data)"
            />
        </div>
    );
};

export default PricingInformation;
